import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS, getFetchConfig } from '../config/api';
import { getImage } from '../assets/images';
import { Plus, X, Star, Asterisk, ArrowUp, ArrowDown, Trash } from 'phosphor-react';
import './ProductImageManager.css';

const ProductImageManager = ({ productId, isOpen, onClose, onImagesUpdate }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [availableImages, setAvailableImages] = useState([]);

  // Carregar imagens do produto
  const loadProductImages = async () => {
    if (!productId) return;
    
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.PRODUCT_IMAGES(productId), getFetchConfig('GET'));
      const data = await response.json();
      
      if (data.status === 'ok') {
        setImages(data.images || []);
      } else {
        setError('Erro ao carregar imagens: ' + (data.error || 'Erro desconhecido'));
      }
    } catch (error) {
      setError('Erro ao carregar imagens: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Carregar imagens disponíveis
  const loadAvailableImages = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.IMAGES, getFetchConfig('GET'));
      const data = await response.json();
      
      if (data.status === 'ok') {
        setAvailableImages(data.images || []);
      }
    } catch (error) {
      console.error('Erro ao carregar imagens disponíveis:', error);
    }
  };

  useEffect(() => {
    if (isOpen && productId) {
      loadProductImages();
      loadAvailableImages();
    }
  }, [isOpen, productId]);

  // Adicionar nova imagem
  const handleAddImage = async () => {
    if (!newImageUrl.trim()) {
      setError('Por favor, selecione uma imagem');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(API_ENDPOINTS.PRODUCT_IMAGES(productId), 
        getFetchConfig('POST', {
          imageUrl: newImageUrl,
          isPrimary: images.length === 0, // Primeira imagem é sempre primária
          displayOrder: images.length
        })
      );
      
      const data = await response.json();
      
      if (data.status === 'ok') {
        setSuccess('Imagem adicionada com sucesso!');
        setNewImageUrl('');
        await loadProductImages();
        if (onImagesUpdate) onImagesUpdate();
      } else {
        setError('Erro ao adicionar imagem: ' + (data.error || 'Erro desconhecido'));
      }
    } catch (error) {
      setError('Erro ao adicionar imagem: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Marcar como imagem primária
  const handleSetPrimary = async (imageId) => {
    try {
      setLoading(true);
      setError('');
      
      const imageToUpdate = images.find(img => img.id === imageId);
      if (!imageToUpdate) return;
      
      const response = await fetch(API_ENDPOINTS.PRODUCT_IMAGE(productId, imageId), 
        getFetchConfig('PUT', {
          imageUrl: imageToUpdate.image_url,
          isPrimary: true,
          displayOrder: imageToUpdate.display_order
        })
      );
      
      const data = await response.json();
      
      if (data.status === 'ok') {
        setSuccess('Imagem primária atualizada!');
        await loadProductImages();
        if (onImagesUpdate) onImagesUpdate();
      } else {
        setError('Erro ao atualizar imagem: ' + (data.error || 'Erro desconhecido'));
      }
    } catch (error) {
      setError('Erro ao atualizar imagem: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Alterar ordem da imagem
  const handleChangeOrder = async (imageId, direction) => {
    const currentIndex = images.findIndex(img => img.id === imageId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= images.length) return;
    
    try {
      setLoading(true);
      setError('');
      
      const imageToUpdate = images[currentIndex];
      const response = await fetch(API_ENDPOINTS.PRODUCT_IMAGE(productId, imageId), 
        getFetchConfig('PUT', {
          imageUrl: imageToUpdate.image_url,
          isPrimary: imageToUpdate.is_primary,
          displayOrder: newIndex
        })
      );
      
      const data = await response.json();
      
      if (data.status === 'ok') {
        await loadProductImages();
        if (onImagesUpdate) onImagesUpdate();
      } else {
        setError('Erro ao alterar ordem: ' + (data.error || 'Erro desconhecido'));
      }
    } catch (error) {
      setError('Erro ao alterar ordem: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Remover imagem
  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Tem certeza que deseja remover esta imagem?')) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(API_ENDPOINTS.PRODUCT_IMAGE(productId, imageId), 
        getFetchConfig('DELETE')
      );
      
      const data = await response.json();
      
      if (data.status === 'ok') {
        setSuccess('Imagem removida com sucesso!');
        await loadProductImages();
        if (onImagesUpdate) onImagesUpdate();
      } else {
        setError('Erro ao remover imagem: ' + (data.error || 'Erro desconhecido'));
      }
    } catch (error) {
      setError('Erro ao remover imagem: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="image-manager-overlay">
      <div className="image-manager-modal">
        <div className="image-manager-header">
          <h3>Gerenciar Imagens do Produto</h3>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="image-manager-content">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {/* Adicionar nova imagem */}
          <div className="add-image-section">
            <h4>Adicionar Nova Imagem</h4>
            <div className="add-image-form">
              <select 
                value={newImageUrl} 
                onChange={(e) => setNewImageUrl(e.target.value)}
                className="image-select"
              >
                <option value="">Selecione uma imagem...</option>
                {availableImages.map(image => (
                  <option key={image} value={image}>{image}</option>
                ))}
              </select>
              <button 
                onClick={handleAddImage} 
                disabled={loading || !newImageUrl.trim()}
                className="btn-add-image"
              >
                <Plus size={16} />
                Adicionar
              </button>
            </div>
            {newImageUrl && (
              <div className="image-preview">
                <img src={getImage(newImageUrl)} alt="Preview" />
              </div>
            )}
          </div>

          {/* Lista de imagens existentes */}
          <div className="existing-images-section">
            <h4>Imagens Existentes ({images.length})</h4>
            
            {loading && <div className="loading">Carregando...</div>}
            
            {!loading && images.length === 0 && (
              <div className="no-images">Nenhuma imagem adicionada ainda</div>
            )}
            
            {!loading && images.length > 0 && (
              <div className="images-grid">
                {images
                  .sort((a, b) => a.display_order - b.display_order)
                  .map((image, index) => (
                  <div key={image.id} className="image-item">
                    <div className="image-preview">
                      <img src={getImage(image.image_url)} alt={`Imagem ${index + 1}`} />
                      {image.is_primary && (
                        <div className="primary-badge">
                          <Asterisk size={16} />
                          Primária
                        </div>
                      )}
                    </div>
                    
                    <div className="image-info">
                      <div className="image-name">{image.image_url}</div>
                      <div className="image-order">Ordem: {image.display_order + 1}</div>
                    </div>
                    
                    <div className="image-actions">
                      {!image.is_primary && (
                        <button
                          onClick={() => handleSetPrimary(image.id)}
                          className="btn-set-primary"
                          title="Marcar como primária"
                        >
                          <Star size={16} />
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleChangeOrder(image.id, 'up')}
                        disabled={index === 0}
                        className="btn-move"
                        title="Mover para cima"
                      >
                        <ArrowUp size={16} />
                      </button>
                      
                      <button
                        onClick={() => handleChangeOrder(image.id, 'down')}
                        disabled={index === images.length - 1}
                        className="btn-move"
                        title="Mover para baixo"
                      >
                        <ArrowDown size={16} />
                      </button>
                      
                      <button
                        onClick={() => handleDeleteImage(image.id)}
                        className="btn-delete"
                        title="Remover imagem"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductImageManager;
